import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseAmount, type BranchInput, type BranchRecordDto, type CurrencyDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf, fieldText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag, money } from "./common";
import { DemoLockNotice } from "../../components/Demo";

// Settings › Branches, ported from the prototype (erp-settings.js "/settings/branches"): the audited columns and
// the New Store dialog (name in Arabic and English, mobile number, addresses, monthly expense, currency).

// The amount is kept as the typed text and sent as text, so it is checked and stored exactly (parseAmount).
type Draft = Omit<BranchInput, "monthlyExpense"> & { monthlyExpense: string };

function BranchForm({ branch, currencies, onClose, onSaved }: { branch: BranchRecordDto | null; currencies: CurrencyDto[]; onClose(): void; onSaved(b: BranchRecordDto, created: boolean): void }) {
  const { t, L } = useLang();
  const [v, setV] = useState<Draft>(() => branch
    ? { nameAr: branch.nameAr, nameEn: branch.nameEn, phone: branch.phone, addressAr: branch.addressAr, addressEn: branch.addressEn, monthlyExpense: String(branch.monthlyExpense), currency: branch.currency }
    : { nameAr: "", nameEn: "", phone: "", addressAr: "", addressEn: "", monthlyExpense: "0", currency: "SAR" });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Draft>(k: K, x: Draft[K]) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };

  async function save() {
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    if (v.phone.trim() && !/^0\d{8,9}$/.test(v.phone.trim())) e.phone = t("phoneFormat");
    const amount = parseAmount(v.monthlyExpense);
    if ("error" in amount) e.monthlyExpense = amount.error === "out_of_range" ? t("amountTooLarge") : fieldText(t, amount.error);
    if (!v.currency) e.currency = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try {
      const body: BranchInput = { ...v, monthlyExpense: v.monthlyExpense.trim() };
      onSaved(branch ? await api<BranchRecordDto>("PUT", `/settings/branches/${branch.id}`, body) : await api<BranchRecordDto>("POST", "/settings/branches", body), !branch);
    } catch (err) {
      setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err));
    } finally { setBusy(false); }
  }
  const txt = (k: "nameAr" | "nameEn" | "phone" | "addressAr" | "addressEn", label: string, o: { req?: boolean; ltr?: boolean } = {}) => (
    <Field label={label} req={o.req} error={errs[k]} htmlFor={`ff-${k}`}>
      <input id={`ff-${k}`} className={`control ${o.ltr ? "num" : ""}`} style={{ textAlign: "start" }} dir={o.ltr ? "ltr" : undefined} type={k === "phone" ? "tel" : "text"} value={v[k]} onChange={(e) => set(k, e.target.value)} autoComplete="off" />
    </Field>
  );
  return (
    <Modal title={t(branch ? "editBranch" : "newBranch")} wide onClose={onClose} onSubmit={save} alert={alert} busy={busy}>
      {txt("nameAr", t("nameAr"), { req: true })}{txt("nameEn", t("nameEn"), { req: true })}{txt("phone", t("mobileNumber"), { ltr: true })}
      {txt("addressAr", t("addressAr"))}{txt("addressEn", t("addressEn"))}
      <Field label={t("monthlyExpense")} error={errs.monthlyExpense} htmlFor="ff-monthly">
        <input id="ff-monthly" className="control num" style={{ textAlign: "start" }} type="number" min={0} step="any" value={v.monthlyExpense} onChange={(e) => set("monthlyExpense", e.target.value)} />
      </Field>
      <Field label={t("currencyF")} req error={errs.currency} htmlFor="ff-currency">
        <select id="ff-currency" className="control" value={v.currency} onChange={(e) => set("currency", e.target.value)}>
          <option value="">{t("choose")}</option>{currencies.map((c) => <option key={c.code} value={c.code}>{`${c.code} · ${L(c)}`}</option>)}
        </select>
      </Field>
    </Modal>
  );
}

export function Branches() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [rows, setRows] = useState<BranchRecordDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);
  const [editing, setEditing] = useState<BranchRecordDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<BranchRecordDto | null>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try { setRows(await api<BranchRecordDto[]>("GET", `/settings/branches${filters.name ? `?q=${encodeURIComponent(filters.name)}` : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [filters, t]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api<CurrencyDto[]>("GET", "/currencies").then(setCurrencies, () => setCurrencies([])); }, []);

  async function doDelete(b: BranchRecordDto) {
    setDeleting(null);
    try { await api("DELETE", `/settings/branches/${b.id}`); toast(t("deletedOk", { x: L(b) })); await load(); void s.refresh(); }
    catch (e) { toast(errorText(t, e)); }
  }

  const admin = !!s.me?.user.administrator;
  const columns: Column<BranchRecordDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (b) => <span className="cell-name" lang="ar" dir="rtl">{b.nameAr}</span>, csv: (b) => b.nameAr },
    { k: "en", label: t("nameEn"), cell: (b) => <span lang="en" dir="ltr">{b.nameEn}</span>, csv: (b) => b.nameEn },
    { k: "phone", label: t("phone"), cell: (b) => <span className="num">{b.phone || "–"}</span>, csv: (b) => b.phone },
    { k: "addrAr", label: t("addressAr"), cell: (b) => <span className="trunc" title={b.addressAr}>{b.addressAr || "–"}</span>, csv: (b) => b.addressAr },
    { k: "addrEn", label: t("addressEn"), cell: (b) => <span className="trunc" title={b.addressEn}>{b.addressEn || "–"}</span>, csv: (b) => b.addressEn },
    { k: "currency", label: t("currencyF"), cell: (b) => b.currency, csv: (b) => b.currency },
    { k: "monthly", label: t("monthlyExpense"), end: true, cell: (b) => money(b.monthlyExpense), csv: (b) => b.monthlyExpense }
  ];
  // Creating a branch reaches beyond a non-administrator's own branch, so the server refuses it; the button says so.
  const newBtn = s.can("storeAdd") && admin
    ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newStore")}</button>
    : <LockedButton label={t("newStore")} title={s.can("storeAdd") ? t("adminOnlyBranches") : t("noPermissionFor", { p: "storeAdd" })} />;

  return (
    <Shell navKey="/settings/branches">
      <DemoLockNotice />
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setBranches")]]} title={t("settingsBranches")} />
      <ListView rows={rows} error={failed} columns={columns} file="branches" testId="branches-table" onRefresh={() => void load()}
        search={[{ k: "name", label: t("name"), type: "text" }]} filters={filters} onFilters={setFilters}
        quick={(b) => `${b.nameEn} ${b.nameAr} ${b.phone}`} newButton={newBtn}
        empty={{ icon: "store", title: t("emptyTitle"), body: t("emptyBody") }}
        actions={(b) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(b), ...(s.can("storeUpdate") ? {} : lockTag(t)) },
          { label: t("barcodeSettings"), icon: "barcode", run: () => nav(`/branch-barcode/${b.id}`), ...(s.can("storeBarcode") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(b), ...(!s.can("storeDelete") ? lockTag(t) : !admin ? { disabled: true, title: t("adminOnlyBranches") } : {}) }
        ]} />
      {editing && <BranchForm branch={editing === "new" ? null : editing} currencies={currencies} onClose={() => setEditing(null)} onSaved={(b, created) => {
        setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(b) })); void load(); void s.refresh();
      }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
    </Shell>
  );
}
